
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { HealthJournal } from "@/types/database.types";
import { useEffect } from "react";

export const useJournalData = (
  searchTerm: string,
  filter: string,
  sort: string
) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Configuration des mises à jour en temps réel
  useEffect(() => {
    const channel = supabase
      .channel('journal-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'health_journal'
        },
        (payload) => {
          console.log('Changement détecté:', payload);
          queryClient.invalidateQueries({ queryKey: ["journal"] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [queryClient]);

  const { data: entries = [], isLoading } = useQuery({
    queryKey: ["journal", searchTerm, filter, sort],
    queryFn: async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error("Non authentifié");

        const { data, error } = await supabase
          .from("health_journal")
          .select("*")
          .eq('user_id', user.id)
          .order("date", { ascending: sort === 'oldest' });
        
        if (error) {
          toast({
            title: "Erreur",
            description: "Impossible de charger les entrées du journal",
            variant: "destructive",
          });
          throw error;
        }
        return data || [];
      } catch (error) {
        console.error("Error fetching journal data:", error);
        return [];
      }
    },
  });

  const moodData = entries?.map(entry => ({
    date: entry.date ? new Date(entry.date).toLocaleDateString('fr-FR') : 'Date inconnue',
    mood: entry.mood === 'EXCELLENT' ? 5 : 
          entry.mood === 'GOOD' ? 4 :
          entry.mood === 'NEUTRAL' ? 3 :
          entry.mood === 'TIRED' ? 2 : 1
  })) || [];

  const symptomsFrequency = entries?.reduce((acc: Record<string, number>, entry) => {
    if (entry.symptoms && Array.isArray(entry.symptoms)) {
      entry.symptoms.forEach(symptom => {
        if (symptom) {
          acc[symptom] = (acc[symptom] || 0) + 1;
        }
      });
    }
    return acc;
  }, {}) || {};

  const filteredAndSortedEntries = filterAndSortEntries(entries, searchTerm, filter, sort);

  return {
    entries: filteredAndSortedEntries,
    isLoading,
    moodData,
    symptomsFrequency,
    totalEntries: entries?.length || 0
  };
};

const filterAndSortEntries = (
  entries: HealthJournal[] | null,
  searchTerm: string,
  filter: string,
  sort: string
): HealthJournal[] | null => {
  if (!entries || !Array.isArray(entries)) return [];

  return entries
    .filter((entry) => {
      if (!entry) return false;
      
      const searchLower = (searchTerm || '').toLowerCase();
      const matchesSearch = 
        (entry.mood && entry.mood.toLowerCase().includes(searchLower)) ||
        (entry.notes && entry.notes.toLowerCase().includes(searchLower)) ||
        (entry.symptoms && Array.isArray(entry.symptoms) && 
          entry.symptoms.some((symptom) => symptom && symptom.toLowerCase().includes(searchLower))) ||
        // Also search in new fields
        (entry.energy_level && entry.energy_level.toLowerCase().includes(searchLower)) ||
        (entry.sleep_quality && entry.sleep_quality.toLowerCase().includes(searchLower)) ||
        (entry.physical_activity && entry.physical_activity.toLowerCase().includes(searchLower)) ||
        (entry.medication_adherence && entry.medication_adherence.toLowerCase().includes(searchLower));

      if (!matchesSearch) return false;

      if (!entry.date) return false;
      
      const entryDate = new Date(entry.date);
      const now = new Date();
      
      switch (filter) {
        case "thisWeek":
          const weekAgo = new Date(now.setDate(now.getDate() - 7));
          return entryDate >= weekAgo;
        case "thisMonth":
          return entryDate.getMonth() === now.getMonth() && 
                 entryDate.getFullYear() === now.getFullYear();
        case "withSymptoms":
          return entry.symptoms && Array.isArray(entry.symptoms) && entry.symptoms.length > 0;
        default:
          return true;
      }
    })
    .sort((a, b) => {
      if (!a.date || !b.date) return 0;
      
      switch (sort) {
        case "oldest":
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        case "moodAsc":
          return (a.mood || '').localeCompare(b.mood || '');
        case "moodDesc":
          return (b.mood || '').localeCompare(a.mood || '');
        default: // newest
          return new Date(b.date).getTime() - new Date(a.date).getTime();
      }
    });
};
